import Button from "@ui/button/Button"
import { EntityInput } from "./EntityInput"
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react"
import { toast } from "react-toastify"
import Config from "src/Config"
import { superAdminFetch as fetch } from "src/services/superAdminFetch"
import {
	parseNonNegativeAmountToMinorUnits,
	parsePositiveAmountToMinorUnits
} from "src/utils/amounts"
import {
	createCardBlockRequest,
	createDbBalanceSetRequest,
	createTrxBalanceAdjustmentRequest,
	createUserStateRequest,
	formatMinorCurrency,
	isSettledDifference,
	SystemBalanceDirection,
	SystemBalanceTarget,
	SystemMutationRequest,
	SystemUserAction
} from "src/utils/systemBalance"
import { cardDisplayStatus, isCardBlocking } from "src/utils/cardStatus"
import { T } from "../adminTheme"
import { AdminModal } from "./AdminModal"
import { Callout, DangerZone, Disclosure, Fact, Facts, Fields, Page, Panel, Row, State, StatusBadge } from "./adminUi"
import { UserOverviewData } from "./UserOverviewData"

interface ReconciliationCard {
	uid: string
	name: string
	mask: string
	san: string
	status: string
	blocking?: boolean
	db_balance_cents: number
	trx_balance_cents: number
	provider_balance_cents: number | null
	provider_used_cents: number | null
	db_minus_trx_cents: number
	db_minus_provider_cents: number | null
	trx_transactions_count: number
	trx_transactions: unknown[]
	provider_transactions_count: number
	provider_transactions: unknown[]
}

interface ReconciliationBuyer {
	uid: string
	name: string
	team_uid: string | null
	team_name: string | null
	trx_email: string | null
	db_team_balance_cents: number
	trx_balance_cents: number
	db_minus_trx_cents: number
	trx_transactions_count: number
	trx_transactions: unknown[]
	cards_count: number
	cards_db_total_cents: number
	cards_trx_total_cents: number
	cards_provider_total_cents: number
	cards: ReconciliationCard[]
}

export interface SystemReconciliationReport {
	issuer?: number
	currency: string
	company: {
		uid: string
		name: string
		db_balance_cents: number
		trx_balance_cents: number
		db_minus_trx_cents: number
		trx_transactions_count: number
		trx_transactions: unknown[]
	}
	totals: {
		buyers_db_total_cents: number
		buyers_trx_total_cents: number
		cards_db_total_cents: number
		cards_trx_total_cents: number
		cards_provider_total_cents: number
	}
	buyers_count: number
	buyers: ReconciliationBuyer[]
}

type BalanceAction =
	| {
			kind: "set-db"
			target: SystemBalanceTarget
			uid: string
			label: string
			teamUid?: string
			currentCents: number | null
	  }
	| {
			kind: "adjust-trx"
			target: SystemBalanceTarget
			direction: SystemBalanceDirection
			uid: string
			label: string
			currentCents: number | null
	  }
	| {
			kind: "block-card"
			uid: string
			label: string
			buyerDestinationAvailable: boolean
	  }
	| {
			kind: "user-state"
			action: SystemUserAction
			uid: string
			label: string
	  }

interface ApiEnvelope<T> {
	data?: T
	error?: { message?: string; details?: unknown }
}

interface OperationResult {
	operation_uid: string
	card_uid: string
	destination: "buyer" | "company"
	note?: string
}

const getApiError = <T,>(payload: ApiEnvelope<T>) => {
	if (!payload.error) return null
	const details =
		typeof payload.error.details === "string"
			? payload.error.details
			: payload.error.details
				? JSON.stringify(payload.error.details)
				: ""
	return [payload.error.message, details].filter(Boolean).join(": ") || "Unknown error"
}

const Money = ({ label, value, currency, tone }: { label: string; value: number | null | undefined; currency: string; tone?: "good" | "bad" }) => (
	<Fact label={label}>
		<span style={{ color: tone === "good" ? T.success : tone === "bad" ? T.danger : undefined }}>{formatMinorCurrency(value, currency)}</span>
	</Fact>
)

const Difference = ({ value, currency }: { value: number | null; currency: string }) => {
	if (value === null || !Number.isFinite(value)) return <Pill tone='muted'>Provider unavailable</Pill>
	const settled = isSettledDifference(value)
	return <Pill tone={settled ? "good" : "bad"}>{settled ? "Matched" : `Difference ${formatMinorCurrency(value, currency)}`}</Pill>
}

/** Same look as StatusBadge: calm when fine or unknown, a coloured pill only for a problem. */
const Pill = ({ tone, children }: { tone: "good" | "bad" | "muted"; children: ReactNode }) => {
	const base = { display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", fontSize: 12.5 } as const
	if (tone === "muted") return <span style={{ ...base, color: T.muted }}>{children}</span>
	const color = tone === "good" ? T.success : T.danger
	const dot = <i aria-hidden style={{ width: 6, height: 6, borderRadius: 6, background: color, flex: "none" }} />
	if (tone === "good") return <span style={{ ...base, color: T.text2 }}>{dot}{children}</span>
	return <span style={{ ...base, padding: "1px 9px", borderRadius: 999, fontWeight: 550, color, background: T.dangerSoft, boxShadow: `inset 0 0 0 1px ${T.dangerLine}` }}>{dot}{children}</span>
}

const RawTransactions = ({ label, transactions }: { label: string; transactions: unknown[] }) => (
	<div style={{ marginTop: 14 }}><Disclosure label={`${label} (${transactions.length})`}><UserOverviewData value={transactions} /></Disclosure></div>
)

const actionTitle = (action: BalanceAction) => {
	if (action.kind === "set-db") return "Set saved balance"
	if (action.kind === "adjust-trx")
		return action.direction === "in" ? "Add to ledger" : "Remove from ledger"
	if (action.kind === "block-card") return "Block card and return balance"
	if (action.action === "disable") return "Disable user and freeze cards"
	if (action.action === "unblock") return "Enable user and unfreeze cards"
	return "Hard-block user and return card balances"
}

const actionWarning = (action: BalanceAction) => {
	if (action.kind === "set-db") return "This overwrites the saved balance only. The ledger is not changed."
	if (action.kind === "adjust-trx") return "This creates an adjustment entry in the transaction ledger."
	if (action.kind === "block-card")
		return "CardsPro runs unfreeze → withdraw → block asynchronously. About $5 may remain on the blocked card."
	if (action.action === "disable")
		return "The user is disabled and active cards are frozen. No money is withdrawn."
	if (action.action === "unblock") return "The user and previously frozen cards are enabled again."
	return "This is irreversible: cards are blocked and their balances are returned to the company."
}

export const SuperAdminBalanceReconciliation = ({
	token,
	companies
}: {
	token: string
	companies: Array<{
		uid: string
		name: string
		balances?: Array<{
			issuer: number
			currency: string
			balance: number | string | null
			is_default?: boolean
		}>
		issuers?: Array<{ issuer: number; issuer_label?: string; is_default?: boolean }>
	}>
}) => {
	const [companyUid, setCompanyUid] = useState("")
	const [issuer, setIssuer] = useState<number | null>(null)
	const [currency, setCurrency] = useState("USD")
	const [report, setReport] = useState<SystemReconciliationReport | null>(null)
	const [loading, setLoading] = useState(false)
	const [pendingAction, setPendingAction] = useState<BalanceAction | null>(null)
	const [actionAmount, setActionAmount] = useState("")
	const [blockDestination, setBlockDestination] = useState<"buyer" | "company">("company")
	const [expandedBuyers, setExpandedBuyers] = useState<Set<string>>(() => new Set())
	const [operation, setOperation] = useState<OperationResult | null>(null)
	const requestInFlight = useRef(false)

	const selectedCompany = useMemo(
		() => companies.find(company => company.uid === companyUid),
		[companies, companyUid]
	)
	/** Balances carry the currency; without them fall back to the connected issuers (USD by default). */
	const accountsOf = (company?: (typeof companies)[number]) => {
		const balances = (company?.balances ?? []).filter(account => Number.isInteger(Number(account.issuer)) && Number(account.issuer) > 0)
		const label = (issuer: number) => company?.issuers?.find(item => item.issuer === issuer)?.issuer_label
		return (balances.length ? balances : (company?.issuers ?? []).map(item => ({ issuer: item.issuer, currency: "USD", balance: null, is_default: item.is_default })))
			.map(account => ({ ...account, label: label(account.issuer) ?? `Issuer ${account.issuer}` }))
	}

	const issuerAccounts = useMemo(
		() => accountsOf(selectedCompany),
		[selectedCompany] // eslint-disable-line react-hooks/exhaustive-deps
	)
	const selectedIssuerAccount = issuerAccounts.find(account => account.issuer === issuer)
	const updateCompanyUid = (nextUid: string) => {
		const company = companies.find(item => item.uid === nextUid)
		const accounts = accountsOf(company)
		const account = accounts.find(item => item.is_default) ?? accounts[0]
		setCompanyUid(nextUid)
		setIssuer(account?.issuer ?? null)
		setCurrency(account?.currency ?? "USD")
		setReport(null)
		setOperation(null)
		setExpandedBuyers(new Set())
	}

	const post = useCallback(
		async <T,>(path: string, body: Record<string, string | number>) => {
			const response = await fetch(Config.scheme.URL_API + path, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify(body)
			})
			const payload = (await response.json()) as ApiEnvelope<T>
			const apiError = getApiError(payload)
			if (!response.ok || apiError) throw new Error(apiError || response.statusText)
			return payload.data as T
		},
		[token]
	)

	const reconcile = useCallback(async () => {
		const trimmedUid = companyUid.trim()
		if (!trimmedUid) {
			toast.warning("Select or enter a company UID")
			return
		}
		if (!Number.isInteger(issuer)) {
			toast.warning("Select an issuer account")
			return
		}
		if (requestInFlight.current) return
		requestInFlight.current = true
		setLoading(true)
		try {
			const nextReport = await post<SystemReconciliationReport>(
				"system/companies/reconcile",
				{ company_uid: trimmedUid, issuer: issuer as number, currency }
			)
			setReport(nextReport)
			setOperation(null)
		} catch (error) {
			console.error(error)
			toast.error(error instanceof Error ? error.message : "Balance check failed")
		} finally {
			requestInFlight.current = false
			setLoading(false)
		}
	}, [companyUid, currency, issuer, post])

	const openAction = (action: BalanceAction) => {
		const defaultIssuer = issuerAccounts.find(account => account.is_default)?.issuer ?? issuerAccounts[0]?.issuer
		if (
			action.kind === "set-db" &&
			action.target !== "card" &&
			issuerAccounts.length > 1 &&
			issuer !== defaultIssuer
		) {
			toast.error("Direct DB overwrite is only safe for the default issuer. Use TRX adjustment for this issuer.")
			return
		}
		setPendingAction(action)
		setActionAmount("")
		setBlockDestination("company")
	}

	const submitAction = async () => {
		if (!pendingAction) return
		if (requestInFlight.current) return
		let request: SystemMutationRequest
		if (pendingAction.kind === "set-db") {
			const parsed = parseNonNegativeAmountToMinorUnits(actionAmount)
			if (typeof parsed.amount !== "number") {
				toast.warning(parsed.error || "Enter a valid amount")
				return
			}
			request = createDbBalanceSetRequest(
				pendingAction.target,
				pendingAction.uid,
				parsed.amount,
				pendingAction.teamUid
			)
		} else if (pendingAction.kind === "adjust-trx") {
			const parsed = parsePositiveAmountToMinorUnits(actionAmount)
			if (typeof parsed.amount !== "number") {
				toast.warning(parsed.error || "Enter a valid amount")
				return
			}
			request = createTrxBalanceAdjustmentRequest(
				pendingAction.target,
				pendingAction.direction,
				pendingAction.uid,
				parsed.amount,
				currency,
				issuer as number
			)
		} else if (pendingAction.kind === "block-card") {
			request = createCardBlockRequest(pendingAction.uid, blockDestination)
		} else {
			request = createUserStateRequest(pendingAction.action, pendingAction.uid)
		}

		requestInFlight.current = true
		setLoading(true)
		try {
			const result = await post<OperationResult | true>(request.path, request.body)
			if (pendingAction.kind === "block-card" && result && result !== true) {
				setOperation(result)
				toast.success("Card block pipeline started")
			} else {
				toast.success("System operation completed")
			}
			setPendingAction(null)
			if (pendingAction.kind !== "block-card") {
				const nextReport = await post<SystemReconciliationReport>(
					"system/companies/reconcile",
					{ company_uid: companyUid.trim(), issuer: issuer as number, currency }
				)
				setReport(nextReport)
			}
		} catch (error) {
			console.error(error)
			toast.error(error instanceof Error ? error.message : "System operation failed")
		} finally {
			requestInFlight.current = false
			setLoading(false)
		}
	}

	const toggleBuyer = (uid: string) => {
		setExpandedBuyers(previous => {
			const next = new Set(previous)
			if (next.has(uid)) next.delete(uid)
			else next.add(uid)
			return next
		})
	}

	const setSaved = (target: SystemBalanceTarget, uid: string, label: string, currentCents: number, teamUid?: string): BalanceAction =>
		({ kind: "set-db", target, uid, label, teamUid, currentCents })
	const adjustLedger = (target: SystemBalanceTarget, direction: SystemBalanceDirection, uid: string, label: string, currentCents: number): BalanceAction =>
		({ kind: "adjust-trx", target, direction, uid, label, currentCents })
	const dangerStyle = { color: T.danger, borderColor: T.dangerLine }
	const hardBlock = pendingAction?.kind === "user-state" && pendingAction.action === "hard-block"

	return (
		<Page>
			<Panel
				title='Company balance check'
				hint='Compare saved, ledger and live provider balances for one issuer account. Amounts are never combined across issuers. No changes are made until you confirm.'
			>
				<Fields>
					<label>Company<EntityInput token={token} kind='company' companies={companies} value={companyUid} disabled={loading} onChange={updateCompanyUid} /></label>
					{issuerAccounts.length ? (
						<label>Issuer account
							<select
								value={selectedIssuerAccount ? String(selectedIssuerAccount.issuer) : ""}
								disabled={loading}
								onChange={event => {
									const account = issuerAccounts.find(item => item.issuer === Number(event.target.value))
									setIssuer(account?.issuer ?? null)
									setCurrency(account?.currency ?? "USD")
									setReport(null)
									setOperation(null)
								}}
							>
								{!selectedIssuerAccount && <option value=''>Choose issuer…</option>}
								{issuerAccounts.map(account => <option key={account.issuer} value={String(account.issuer)}>{`${account.label} · ${account.currency}`}</option>)}
							</select>
						</label>
					) : (
						<label>Issuer number
							<input
								type='number'
								placeholder='2'
								value={issuer ?? ""}
								disabled={loading}
								onChange={event => {
									const parsed = Number(event.target.value)
									setIssuer(Number.isInteger(parsed) && parsed > 0 ? parsed : null)
									setReport(null)
								}}
							/>
						</label>
					)}
					<label>Currency<input value={currency} readOnly style={{ background: T.surface2 }} /></label>
				</Fields>
				<Row style={{ marginTop: 16 }}>
					<Button variant='primarySmall' disabled={loading || !companyUid.trim() || issuer === null} onClick={() => void reconcile()}>
						{loading ? "Checking…" : "Check balances"}
					</Button>
				</Row>
			</Panel>

			{operation && (
				<Callout role='status' tone='warn' title='Card update started'>
					<span>
						Operation {operation.operation_uid} · card {operation.card_uid} · sent to {operation.destination}. Wait about 10 seconds, then check balances again.
					</span>
				</Callout>
			)}

			{report && (
				<Page style={{ opacity: loading ? 0.65 : 1, pointerEvents: loading ? "none" : "auto", transition: `opacity .16s ${T.ease}` }}>
					<Panel
						title={report.company.name}
						hint={`Issuer ${report.issuer ?? issuer} · ${report.company.uid} · ${report.company.trx_transactions_count} ledger records`}
						actions={<Difference value={report.company.db_minus_trx_cents} currency={report.currency} />}
					>
						<Facts>
							<Money label='Company saved balance' value={report.company.db_balance_cents} currency={report.currency} />
							<Money label='Company ledger balance' value={report.company.trx_balance_cents} currency={report.currency} />
							<Money label='Saved minus ledger' value={report.company.db_minus_trx_cents} currency={report.currency} tone={isSettledDifference(report.company.db_minus_trx_cents) ? "good" : "bad"} />
						</Facts>
						<RawTransactions label='Company ledger transactions' transactions={report.company.trx_transactions ?? []} />
						<div style={{ marginTop: 14 }}>
							<DangerZone title='Change company balance' hint='Changes money. Check the numbers above first; every change asks for confirmation.'>
								<Row>
									<Button variant='secondarySmall' onClick={() => openAction(setSaved("company", report.company.uid, report.company.name, report.company.db_balance_cents))}>Set saved balance</Button>
									<Button variant='secondarySmall' onClick={() => openAction(adjustLedger("company", "in", report.company.uid, report.company.name, report.company.trx_balance_cents))}>Add to ledger</Button>
									<Button variant='secondarySmall' onClick={() => openAction(adjustLedger("company", "out", report.company.uid, report.company.name, report.company.trx_balance_cents))}>Remove from ledger</Button>
								</Row>
							</DangerZone>
						</div>
					</Panel>

					<Panel title='Totals across buyers and cards'>
						<Facts>
							<Money label='Buyers saved total' value={report.totals.buyers_db_total_cents} currency={report.currency} />
							<Money label='Buyers ledger total' value={report.totals.buyers_trx_total_cents} currency={report.currency} />
							<Money label='Cards saved total' value={report.totals.cards_db_total_cents} currency={report.currency} />
							<Money label='Cards ledger total' value={report.totals.cards_trx_total_cents} currency={report.currency} />
							<Money label='Cards provider total' value={report.totals.cards_provider_total_cents} currency={report.currency} />
						</Facts>
					</Panel>

					<h3 style={{ marginTop: 6 }}>Buyers ({report.buyers_count})</h3>
					{report.buyers.length === 0 && <Panel><State empty emptyText='No buyers for this company.' /></Panel>}
					{report.buyers.map(buyer => {
						const expanded = expandedBuyers.has(buyer.uid)
						const buyerLabel = buyer.name || buyer.uid
						return (
							<Panel
								key={buyer.uid}
								title={buyerLabel}
								hint={`${buyer.uid} · ${buyer.team_name || "No team"}`}
								actions={<>
									<Difference value={buyer.db_minus_trx_cents} currency={report.currency} />
									<Button variant='secondarySmall' onClick={() => toggleBuyer(buyer.uid)}>{expanded ? "Hide cards" : `Show ${buyer.cards_count} cards`}</Button>
								</>}
							>
								<Facts>
									<Money label='Buyer saved balance' value={buyer.db_team_balance_cents} currency={report.currency} />
									<Money label='Buyer ledger balance' value={buyer.trx_balance_cents} currency={report.currency} />
									<Money label='Cards at provider' value={buyer.cards_provider_total_cents} currency={report.currency} />
								</Facts>
								<RawTransactions label='Buyer ledger transactions' transactions={buyer.trx_transactions ?? []} />
								<div style={{ marginTop: 14 }}>
									<DangerZone title='Change buyer balance or access' hint='Changes money or blocks access. Check the numbers above first; every change asks for confirmation.'>
										<Row>
											<Button variant='secondarySmall' onClick={() => openAction(setSaved("buyer", buyer.uid, buyerLabel, buyer.db_team_balance_cents, buyer.team_uid || undefined))}>Set saved balance</Button>
											<Button variant='secondarySmall' onClick={() => openAction(adjustLedger("buyer", "in", buyer.uid, buyerLabel, buyer.trx_balance_cents))}>Add to ledger</Button>
											<Button variant='secondarySmall' onClick={() => openAction(adjustLedger("buyer", "out", buyer.uid, buyerLabel, buyer.trx_balance_cents))}>Remove from ledger</Button>
											<Button variant='secondarySmall' onClick={() => openAction({ kind: "user-state", action: "disable", uid: buyer.uid, label: buyerLabel })}>Disable + freeze</Button>
											<Button variant='secondarySmall' onClick={() => openAction({ kind: "user-state", action: "unblock", uid: buyer.uid, label: buyerLabel })}>Enable + unfreeze</Button>
											<Button variant='secondarySmall' sx={dangerStyle} onClick={() => openAction({ kind: "user-state", action: "hard-block", uid: buyer.uid, label: buyerLabel })}>Hard block</Button>
										</Row>
									</DangerZone>
								</div>

								{expanded && (
									<div style={{ display: "grid", gap: 12, marginTop: 16, animation: `bo-rise .24s ${T.ease} both` }}>
										{buyer.cards.map(card => {
											const cardLabel = card.name || card.mask || card.uid
											return (
												<Panel
													key={card.uid}
													title={cardLabel}
													hint={`${card.uid} · ${card.mask || card.san || "No mask"}`}
													actions={<><StatusBadge value={cardDisplayStatus(card)} /><Difference value={card.db_minus_provider_cents} currency={report.currency} /></>}
													style={{ background: T.surface2, boxShadow: "none" }}
												>
													<Facts>
														<Money label='Card saved balance' value={card.db_balance_cents} currency={report.currency} />
														<Money label='Card ledger balance' value={card.trx_balance_cents} currency={report.currency} />
														<Money label='Provider live balance' value={card.provider_balance_cents} currency={report.currency} />
														<Money label='Provider used' value={card.provider_used_cents} currency={report.currency} />
													</Facts>
													<RawTransactions label='Card ledger transactions' transactions={card.trx_transactions ?? []} />
													<RawTransactions label='CardsPro transactions' transactions={card.provider_transactions ?? []} />
													<div style={{ marginTop: 14 }}>
														<DangerZone title='Change card balance' hint='Changes money. Check the numbers above first; every change asks for confirmation.'>
															<Row>
																<Button variant='secondarySmall' onClick={() => openAction(setSaved("card", card.uid, cardLabel, card.db_balance_cents))}>Set saved balance</Button>
																<Button variant='secondarySmall' onClick={() => openAction(adjustLedger("card", "in", card.uid, cardLabel, card.trx_balance_cents))}>Add to ledger</Button>
																<Button variant='secondarySmall' onClick={() => openAction(adjustLedger("card", "out", card.uid, cardLabel, card.trx_balance_cents))}>Remove from ledger</Button>
																<Button variant='secondarySmall' disabled={card.status.toLowerCase() === "blocked" || isCardBlocking(card)} sx={dangerStyle} onClick={() => openAction({ kind: "block-card", uid: card.uid, label: cardLabel, buyerDestinationAvailable: Boolean(buyer.team_uid) })}>Block + return</Button>
															</Row>
														</DangerZone>
													</div>
												</Panel>
											)
										})}
									</div>
								)}
							</Panel>
						)
					})}
				</Page>
			)}

			{pendingAction && (
				<AdminModal title={actionTitle(pendingAction)} subtitle={pendingAction.label} width={520} onClose={() => { if (!loading) setPendingAction(null) }}>
					<Callout tone={hardBlock ? "danger" : "warn"}>{actionWarning(pendingAction)}</Callout>
					{(pendingAction.kind === "set-db" || pendingAction.kind === "adjust-trx") && (
						<>
							<Facts>
								<Money label={pendingAction.kind === "set-db" ? "Current saved balance" : "Current ledger balance"} value={pendingAction.currentCents} currency={currency} />
							</Facts>
							<Fields>
								<label>{pendingAction.kind === "set-db" ? "New absolute balance" : "Adjustment amount"}
									<input type='number' placeholder='0.00' value={actionAmount} disabled={loading} onChange={event => setActionAmount(event.target.value)} />
								</label>
								<label>Currency<input value={currency} readOnly style={{ background: T.surface2 }} /></label>
							</Fields>
						</>
					)}
					{pendingAction.kind === "block-card" && (
						<Fields>
							<label>Return balance to
								<select value={blockDestination} disabled={loading} onChange={event => setBlockDestination(event.target.value === "buyer" ? "buyer" : "company")}>
									<option value='company'>Company</option>
									{pendingAction.buyerDestinationAvailable && <option value='buyer'>Buyer</option>}
								</select>
							</label>
						</Fields>
					)}
					<Row>
						<Button variant={hardBlock ? "dangerSmall" : "primarySmall"} disabled={loading} onClick={() => void submitAction()}>{loading ? "Processing…" : "Confirm operation"}</Button>
						<Button variant='secondarySmall' disabled={loading} onClick={() => setPendingAction(null)}>Cancel</Button>
					</Row>
				</AdminModal>
			)}
		</Page>
	)
}
